import { useState, useEffect } from "react";
import { MessageCircle, X, Phone } from "lucide-react";
import { trackEvent, GA_EVENTS } from "./GoogleAnalytics";

export const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  // Configuration
  const phoneNumber = "447398873176"; // UK number in international format
  const message = "Hello! I'm interested in your premium plumbing services.";

  // Show widget after page loads (premium entrance effect)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []); // Empty deps is correct here - we only want this to run once on mount

  const handleWhatsAppClick = () => {
    // Track WhatsApp click
    trackEvent(GA_EVENTS.WHATSAPP_CLICKED, {
      source: 'floating_widget'
    });
    
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setIsOpen(false);
  };

  const handleCallClick = () => {
    // Track emergency call click
    trackEvent(GA_EVENTS.EMERGENCY_CALL_CLICKED, {
      source: 'whatsapp_widget',
      phone: phoneNumber
    });
    
    window.location.href = `tel:+${phoneNumber}`;
  };

  if (!isVisible) return null;

  return (
    <>
      {/* WhatsApp Widget Container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        
        {/* Popup Card */}
        {isOpen && (
          <div className="mb-2 bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl p-6 w-80 sm:w-96 animate-in slide-in-from-bottom-5 border border-sage-200/50 backdrop-blur-sm">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">💬 Chat with Magic C</h3>
                <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  We typically reply instantly
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors hover:rotate-90 duration-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Message Preview */}
            <div className="bg-gradient-to-r from-sage-50 to-sage-100/50 rounded-xl p-4 mb-4 border border-sage-200/50">
              <p className="text-sm text-gray-700 leading-relaxed">
                👋 <span className="font-semibold">Hi there!</span> Need premium plumbing, heating, or emergency services? 
                <br />
                <span className="text-sage-700 font-medium mt-2 inline-block">
                  Let's chat on WhatsApp!
                </span>
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2.5">
              <button
                onClick={handleWhatsAppClick}
                className="w-full bg-gradient-to-r from-[#25D366] to-[#22c55e] hover:from-[#22c55e] hover:to-[#25D366] text-white font-semibold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95"
              >
                <MessageCircle className="h-5 w-5" />
                <span>Start WhatsApp Chat</span>
              </button>

              <button
                onClick={handleCallClick}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all border-2 border-gray-200 hover:border-sage-400 hover:text-sage-700"
              >
                <Phone className="h-4 w-4" />
                <span>Call Us: 07398 873176</span>
              </button>
            </div>
            
            <p className="text-xs text-gray-500 mt-4 text-center font-medium">
              🕐 Available 24/7 for emergencies
            </p>
          </div>
        )}

        {/* Floating WhatsApp Button - Premium Design */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="group relative bg-gradient-to-br from-[#25D366] to-[#22c55e] hover:from-[#22c55e] hover:to-[#25D366] text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center"
          aria-label="WhatsApp Chat"
        >
          {/* Pulse Ring Effect */}
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20"></span>
          
          {/* Icon */}
          {isOpen ? (
            <X className="h-6 w-6 relative z-10 transition-transform group-hover:rotate-90 duration-300" />
          ) : (
            <MessageCircle className="h-6 w-6 relative z-10 transition-transform group-hover:scale-110 duration-300" />
          )}
          
          {/* Notification Badge (Optional - can be enabled for unread messages) */}
          {!isOpen && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg animate-bounce">
              1
            </span>
          )}
        </button>
      </div>
    </>
  );
};
